/**
 * Route-change page-view tracking.
 *
 * Fires a `page_view` activity event on every pathname change. Mount this
 * hook exactly once, inside the router context — see `App.tsx`.
 *
 * The human-readable `PAGE_NAMES` map is ported from the legacy
 * `frontend/static/js/core/analytics.js`. In-app tab/view switches that the
 * legacy app also tracked as synthetic `page_view`s (MARS forecasts tabs,
 * MARS portfolio tabs/toggles, Economic-Outlook view/country changes) are
 * recorded directly by those components via `trackActivity`, not here.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackActivity } from './tracker';

/**
 * Pathname → human-readable page name.
 *
 * Ported from the legacy analytics map and updated to the React-router URL
 * scheme (e.g. `/central-bank/voting-analysis`, `/mars-portfolio-dashboard`).
 * A path absent from this map still fires a `page_view`; the pathname itself
 * is used as the description fallback.
 */
export const PAGE_NAMES: Record<string, string> = {
  '/': 'Overview Dashboard',
  '/economic-outlook': 'Econ Outlook: Activity, US, Nowcast',
  '/econ-activity': 'Econ Outlook: Activity, US, Nowcast',
  '/central-bank': 'Central Bank Tracker',
  '/central-bank/timeseries': 'Time Series',
  '/central-bank/voting-analysis': 'FOMC Voting Analysis',
  '/forecasts': 'Forecasts',
  '/forecasts/macro_economic': 'Forecasts: Macro Economic',
  '/forecasts/returns': 'Forecasts: Returns',
  '/forecasts/volatilities': 'Forecasts: Volatilities',
  '/expected-returns': 'Forecasts',
  '/mars-forecasts': 'Forecasts',
  '/mars-portfolio-dashboard': 'Portfolio: MARS QMAS',
  '/mars-live': 'MARS Live',
  '/disclaimer': 'Disclaimer',
  '/admin/login-analytics': 'Login Analytics',
  '/admin/user-activity': 'User Activity',
};

/** Resolve a pathname to its human-readable page name. */
export function pageNameFor(pathname: string): string {
  return PAGE_NAMES[pathname] ?? pathname;
}

/**
 * Fire a `page_view` event whenever the router pathname changes (including
 * the initial mount). Anonymous users are gated out inside `trackActivity`.
 */
export function usePageViewTracking(): void {
  const { pathname } = useLocation();
  // Guard against firing twice for the same pathname (React 18 StrictMode
  // double-invokes effects in development).
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    if (lastPath.current === pathname) return;
    lastPath.current = pathname;
    trackActivity('page_view', pageNameFor(pathname), {});
  }, [pathname]);
}
